"use client";

import { useEffect, useState } from "react";
import ReactTextareaAutosize from "react-textarea-autosize";
import { toast } from "sonner";
import { X } from "lucide-react";

import { StudioCollection, StudioPost, StudioPreference } from "@prisma/client";
import { Button, Dialog, DialogContent, DialogTitle, Switch } from "ui";

import { trpc } from "@/lib/trpc/client";
import {
  sortPostsByCollectionOrder,
  toPostSortOrder,
  type CollectionPostSortOrder,
} from "@/lib/collection-post-order";
import { FullCollection } from "@/lib/types";
import { getBlurDataURL, placeholderBlurhash } from "@/lib/utils";
import BlurImage from "@/components/blur-image";
import Uploader from "@/components/form/uploader";
import CollectionPostsManager from "@/components/modal/admin/collection-posts-manager";
import LayoutPicker from "@/components/admin/layout-picker";
import LoadingDots from "@/components/icons/loading-dots";

interface CollectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  studioId: string;
  collection?: StudioCollection;
  preferences: StudioPreference;
  editAfterCreate?: boolean;
  onSaved?: (collection?: StudioCollection) => void;
}

export default function CollectionModal({
  open,
  onOpenChange,
  studioId,
  collection,
  editAfterCreate = true,
  onSaved,
}: CollectionModalProps) {
  const utils = trpc.useUtils();
  const createCollection = trpc.collections.create.useMutation();
  const updateCollection = trpc.collections.update.useMutation();
  const deleteCollection = trpc.collections.delete.useMutation();
  const setPinnedMutation = trpc.collections.setPinned.useMutation();

  // The collection being edited: the prop (pencil) or the one just created.
  const [editing, setEditing] = useState<StudioCollection | null>(null);
  const mode = editing ? "edit" : "create";

  // The default "All" collection: contents are automatic (every published
  // post authored as the Studio) and it cannot be deleted or hand-curated.
  const isDefaultCollection = Boolean(editing?.isDefault);

  // Shared metadata form state.
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState<string | null>(null);
  const [imageBlurhash, setImageBlurhash] = useState<string | null>(null);
  const [pinned, setPinned] = useState(false);
  const [hideFromFeed, setHideFromFeed] = useState(false);
  const [postSortOrder, setPostSortOrder] =
    useState<CollectionPostSortOrder>("curated");

  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Edit-mode loaded data.
  const [reloadKey, setReloadKey] = useState(0);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [fullCollection, setFullCollection] = useState<FullCollection | null>(
    null,
  );
  const [studioPosts, setStudioPosts] = useState<StudioPost[]>([]);

  // Seed / reset whenever opened. Pencil path provides `collection`.
  useEffect(() => {
    if (!open) return;
    setEditing(collection ?? null);
    setConfirmingDelete(false);
    setName(collection?.name ?? "");
    setDescription(collection?.description ?? "");
    setImage(collection?.image ?? null);
    setImageBlurhash(collection?.imageBlurhash ?? null);
    setPinned(collection?.pinnedToSidebar ?? false);
    setHideFromFeed(collection?.hideFromFeed ?? false);
    setPostSortOrder(toPostSortOrder(collection?.postSortOrder));
    // Re-seed only when opening or switching to a different collection. We
    // intentionally exclude the `collection` object identity so a parent
    // re-render passing a new-but-equal object can't clobber an in-progress
    // edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, collection?.id]);

  // Lazy-load full collection + studio posts whenever we enter edit mode.
  useEffect(() => {
    if (!open || !editing) return;
    let cancelled = false;
    setLoadingEdit(true);
    setLoadError(false);
    Promise.all([
      utils.collections.get.fetch({
        id: editing.id,
      }) as Promise<FullCollection | null>,
      // Collections hold staff-curated content — keep member channel posts
      // and chat messages out of the picker (TRA-1813 narrowing flags).
      // Only published posts are selectable (TRA-1598) — omitting
      // includeDrafts lets the backend's default published-only filter apply.
      // Cast: inferring the tRPC output type inside Promise.all trips TS2589
      // (instantiation depth); the result is re-cast to StudioPost[] below.
      utils.client.posts.list.query({
        staffOnly: true,
        excludeMessages: true,
      }) as unknown as Promise<unknown[]>,
    ])
      .then(([full, posts]) => {
        if (cancelled) return;
        if (!full) {
          setLoadError(true);
          return;
        }
        setFullCollection(full);
        setStudioPosts(
          Array.isArray(posts) ? (posts as unknown as StudioPost[]) : [],
        );
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingEdit(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, editing, reloadKey, utils]);

  const handleUpload = async (file?: File) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await fetch("/api/upload", {
        method: "POST",
        headers: { "content-type": file.type || "application/octet-stream" },
        body: file,
      });
      if (res.status !== 200) {
        toast.error(await res.text());
        return;
      }
      const { url } = await res.json();
      const blurhash = await getBlurDataURL(url);
      setImage(url);
      setImageBlurhash(blurhash);
    } catch {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Name cannot be empty");
      return;
    }
    setSaving(true);
    try {
      const created = (await createCollection.mutateAsync({
        name: trimmed,
        description,
        image,
        imageBlurhash,
      })) as unknown as StudioCollection;
      await utils.collections.list.invalidate();
      toast.success("Collection created");
      if (editAfterCreate) {
        setEditing(created);
        onSaved?.(created);
      } else {
        onOpenChange(false);
        onSaved?.(created);
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to create collection",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMetadata = async () => {
    if (!editing) return;
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Name cannot be empty");
      return;
    }
    setSaving(true);
    try {
      await updateCollection.mutateAsync({
        id: editing.id,
        data: {
          name: trimmed,
          description,
          image,
          imageBlurhash,
          postSortOrder,
        },
      });
      await Promise.all([
        utils.collections.list.invalidate(),
        utils.collections.get.invalidate({ id: editing.id }),
      ]);
      toast.success("Collection saved");
      onSaved?.();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to save collection",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePinned = async (next: boolean) => {
    if (!editing) return;
    setPinned(next);
    try {
      await setPinnedMutation.mutateAsync({ id: editing.id, pinned: next });
      await utils.collections.list.invalidate();
      onSaved?.();
    } catch {
      setPinned(!next);
      toast.error("Failed to update the sidebar pin");
    }
  };

  const handleToggleHideFromFeed = async (next: boolean) => {
    if (!editing) return;
    setHideFromFeed(next);
    try {
      await updateCollection.mutateAsync({
        id: editing.id,
        data: { hideFromFeed: next },
      });
      await utils.collections.list.invalidate();
      onSaved?.();
    } catch {
      setHideFromFeed(!next);
      toast.error("Failed to update the feed setting");
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    setDeleting(true);
    try {
      await deleteCollection.mutateAsync({ id: editing.id });
      await utils.collections.list.invalidate();
      toast.success("Collection deleted");
      onOpenChange(false);
      onSaved?.();
    } catch {
      toast.error("Failed to delete collection");
    } finally {
      setDeleting(false);
    }
  };

  const busy = saving || deleting || uploading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        aria-describedby={undefined}
        className="flex max-h-[85vh] w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl"
      >
        <DialogTitle className="sr-only">
          {mode === "edit" ? "Edit collection" : "Create collection"}
        </DialogTitle>

        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="font-title text-base font-semibold text-foreground">
            {mode === "edit" ? "Edit collection" : "New collection"}
          </h2>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Cover image */}
          <div className="mb-4">
            {image ? (
              <div className="relative h-48 w-full overflow-hidden rounded-lg">
                <BlurImage
                  src={image}
                  blurDataURL={imageBlurhash ?? placeholderBlurhash}
                  alt={name || "Cover image"}
                  className="object-cover"
                  placeholder="blur"
                  fill
                />
                <button
                  type="button"
                  onClick={() => {
                    setImage(null);
                    setImageBlurhash(null);
                  }}
                  aria-label="Remove cover image"
                  className="absolute right-2 top-2 rounded-md bg-black/60 p-1 text-white transition-colors hover:bg-black/80"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <Uploader
                defaultValue={null}
                name="image"
                loading={uploading}
                onChange={async (_: string, file?: File) => handleUpload(file)}
              />
            )}
          </div>

          {/* Name */}
          <input
            type="text"
            placeholder="Collection name"
            value={name}
            maxLength={50}
            autoFocus={mode === "create"}
            onChange={(e) => setName(e.target.value.slice(0, 50))}
            className="w-full border-none bg-transparent px-0 font-title text-2xl font-bold text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0"
          />

          {/* Description */}
          <ReactTextareaAutosize
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-2 w-full resize-none border-none bg-transparent px-0 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0"
          />

          {mode === "edit" && (
            <div className="mt-4 flex items-center justify-between gap-4 rounded-lg border border-border px-3 py-2">
              <div>
                <p className="text-sm font-medium text-foreground">
                  Pin to sidebar
                </p>
                <p className="text-xs text-muted-foreground">
                  Show as a shortcut above channels in the member sidebar
                </p>
              </div>
              <Switch
                checked={pinned}
                onCheckedChange={handleTogglePinned}
                disabled={setPinnedMutation.isLoading}
                aria-label="Pin collection to sidebar"
              />
            </div>
          )}

          {/* The default "All" collection has no join rows (virtual
              membership), so the feed filter can never match it. */}
          {mode === "edit" && !isDefaultCollection && (
            <div className="mt-3 flex items-center justify-between gap-4 rounded-lg border border-border px-3 py-2">
              <div>
                <p className="text-sm font-medium text-foreground">
                  Hide from Home feed
                </p>
                <p className="text-xs text-muted-foreground">
                  Keep these posts out of the community feed — members find them
                  through the collection instead
                </p>
              </div>
              <Switch
                checked={hideFromFeed}
                onCheckedChange={handleToggleHideFromFeed}
                disabled={updateCollection.isLoading}
                aria-label="Hide this collection's posts from the Home feed"
              />
            </div>
          )}

          {mode === "edit" && (
            <div className="mt-3 rounded-lg border border-border px-3 py-2">
              <p className="text-sm font-medium text-foreground">Post order</p>
              <p className="mb-2 text-xs text-muted-foreground">
                {postSortOrder === "curated"
                  ? "Manually ordered posts first, then newest. Applies on save."
                  : "Chronological — any manual ordering is ignored. Applies on save."}
              </p>
              <div className="flex flex-wrap gap-2" role="radiogroup">
                {(
                  [
                    ["curated", "Curated"],
                    ["oldest", "Oldest first"],
                    ["newest", "Newest first"],
                  ] as const
                ).map(([value, optionLabel]) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={postSortOrder === value}
                    onClick={() => setPostSortOrder(value)}
                    className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
                      postSortOrder === value
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-foreground hover:bg-accent"
                    }`}
                  >
                    {optionLabel}
                  </button>
                ))}
              </div>
            </div>
          )}

          {mode === "edit" && (
            <>
              <div className="my-4 h-px w-full bg-border" />
              {loadingEdit ? (
                <div className="flex h-40 items-center justify-center">
                  <LoadingDots />
                </div>
              ) : loadError ? (
                <div className="flex h-40 flex-col items-center justify-center gap-2">
                  <p className="text-sm text-muted-foreground">
                    Unable to load this collection.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setReloadKey((k) => k + 1)}
                  >
                    Retry
                  </Button>
                </div>
              ) : (
                <>
                  {/* Layout */}
                  <div className="mb-6">
                    <LayoutPicker
                      collectionId={editing!.id}
                      initialPreset={
                        (fullCollection as { layoutPreset?: string } | null)
                          ?.layoutPreset ?? "grid"
                      }
                      initialConfig={
                        (
                          fullCollection as {
                            layoutConfig?: Record<string, unknown> | null;
                          } | null
                        )?.layoutConfig ?? null
                      }
                    />
                  </div>

                  {/* Posts — the default collection is not hand-curated */}
                  {isDefaultCollection ? (
                    <p className="text-sm text-muted-foreground">
                      This collection automatically includes every published
                      post by your Studio.
                    </p>
                  ) : (
                    <CollectionPostsManager
                      key={editing!.id}
                      collectionId={editing!.id}
                      studioPosts={studioPosts}
                      // Already-collected posts may have since been
                      // unpublished — pass them separately (draft-inclusive)
                      // so they still render as "in collection" even though
                      // `studioPosts` (the add-picker pool) is published-only.
                      collectionPosts={fullCollection?.posts ?? []}
                      initialPostIds={sortPostsByCollectionOrder(
                        fullCollection?.posts ?? [],
                        fullCollection?.postOrder,
                      ).map((p) => p.id)}
                      onChanged={onSaved}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-border px-6 py-3">
          <div>
            {mode === "edit" &&
              !isDefaultCollection &&
              (confirmingDelete ? (
                <div className="flex items-center gap-2">
                  <Button
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={busy}
                  >
                    {deleting ? <LoadingDots /> : "Confirm delete"}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setConfirmingDelete(false)}
                    disabled={busy}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  onClick={() => setConfirmingDelete(true)}
                  disabled={busy}
                  className="text-destructive hover:text-destructive"
                >
                  Delete
                </Button>
              ))}
          </div>
          <div className="flex items-center gap-2">
            {mode === "create" ? (
              <Button onClick={handleCreate} disabled={busy}>
                {saving ? <LoadingDots /> : "Create"}
              </Button>
            ) : (
              <Button onClick={handleSaveMetadata} disabled={busy}>
                {saving ? <LoadingDots /> : "Save"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
